/**
 * Enhanced Product Features - Complete Interactive System
 * Handles countdown, wishlist, inventory, tabs, size guide, and more
 */

class ProductEnhancedFeatures {
  constructor() {
    this.init();
  }

  init() {
    this.initCountdownTimer();
    this.initWishlist();
    this.initProductTabs();
    this.initInventoryProgress();
    this.initQuantitySelector();
    this.initVariantSwatches();
    this.initMobileSwipeGestures();
    this.initSocialProof();
    this.initBackInStock();
  }

  // Countdown Timer
  initCountdownTimer() {
    const timers = document.querySelectorAll('.countdown-timer');
    
    timers.forEach(timer => {
      const endDate = new Date(timer.dataset.endDate).getTime();
      const endMessage = timer.dataset.endMessage || 'Sale Ended';
      
      const countdown = setInterval(() => {
        const now = new Date().getTime();
        const distance = endDate - now;
        
        if (distance < 0) {
          clearInterval(countdown);
          timer.innerHTML = `<div class="countdown-timer__ended">${endMessage}</div>`;
          return;
        }
        
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        timer.querySelector('[data-days]').textContent = String(days).padStart(2, '0');
        timer.querySelector('[data-hours]').textContent = String(hours).padStart(2, '0');
        timer.querySelector('[data-minutes]').textContent = String(minutes).padStart(2, '0');
        timer.querySelector('[data-seconds]').textContent = String(seconds).padStart(2, '0');
      }, 1000);
    });
  }

  // Wishlist with LocalStorage
  initWishlist() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      const isWishlisted = this.isInWishlist(productId);
      
      if (isWishlisted) {
        button.classList.add('active');
      }
      
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleWishlist(productId, button);
      });
    });
  }

  isInWishlist(productId) {
    const wishlist = JSON.parse(localStorage.getItem('kirei_wishlist') || '[]');
    return wishlist.includes(productId);
  }

  toggleWishlist(productId, button) {
    let wishlist = JSON.parse(localStorage.getItem('kirei_wishlist') || '[]');
    
    if (this.isInWishlist(productId)) {
      wishlist = wishlist.filter(id => id !== productId);
      button.classList.remove('active');
      this.showToast('Removed from wishlist');
    } else {
      wishlist.push(productId);
      button.classList.add('active');
      this.showToast('Added to wishlist');
    }
    
    localStorage.setItem('kirei_wishlist', JSON.stringify(wishlist));
    
    // Trigger custom event
    window.dispatchEvent(new CustomEvent('wishlist:updated', { 
      detail: { productId, action: this.isInWishlist(productId) ? 'added' : 'removed' }
    }));
  }

  showToast(message) {
    let toast = document.querySelector('.wishlist-toast');
    
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'wishlist-toast';
      document.body.appendChild(toast);
    }
    
    toast.textContent = message;
    toast.classList.add('visible');
    
    setTimeout(() => {
      toast.classList.remove('visible');
    }, 3000);
  }

  // Product Tabs (Desktop)
  initProductTabs() {
    const tabsContainer = document.querySelector('.product-tabs');
    if (!tabsContainer) return;
    
    const tabs = tabsContainer.querySelectorAll('.product-tabs__tab');
    const panels = tabsContainer.querySelectorAll('.product-tabs__content');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabNumber = tab.dataset.tab;
        
        // Remove all active states
        tabs.forEach(t => t.classList.remove('active'));
        panels.forEach(p => p.classList.remove('active'));
        
        // Add active to clicked tab and corresponding panel
        tab.classList.add('active');
        const activePanel = tabsContainer.querySelector(`[data-tab-content="${tabNumber}"]`);
        if (activePanel) {
          activePanel.classList.add('active');
        }
      });
    });
  }

  // Inventory Progress Animation
  initInventoryProgress() {
    const progressBars = document.querySelectorAll('.inventory-progress');
    
    progressBars.forEach(bar => {
      const fill = bar.querySelector('.inventory-progress__fill');
      const targetWidth = fill.style.width;
      
      // Animate from 0 to target
      fill.style.width = '0%';
      
      setTimeout(() => {
        fill.style.transition = 'width 1.5s cubic-bezier(0.65, 0, 0.35, 1)';
        fill.style.width = targetWidth;
      }, 300);
    });
  }

  // Enhanced Quantity Selector
  initQuantitySelector() {
    const quantityInputs = document.querySelectorAll('input[name="quantity"]');
    
    quantityInputs.forEach(input => {
      // Create custom controls if needed
      if (!input.closest('.quantity-selector--enhanced')) return;
      
      const container = input.closest('.quantity-selector--enhanced');
      const decreaseBtn = container.querySelector('[data-quantity-decrease]');
      const increaseBtn = container.querySelector('[data-quantity-increase]');
      
      if (decreaseBtn && increaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        
        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const maxValue = parseInt(input.max) || 999;
          if (currentValue < maxValue) {
            input.value = currentValue + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });
  }

  // Variant Swatches with Images
  initVariantSwatches() {
    const swatches = document.querySelectorAll('.variant-swatch--with-image');
    
    swatches.forEach(swatch => {
      swatch.addEventListener('click', function() {
        // Remove active from siblings
        const parent = this.parentElement;
        parent.querySelectorAll('.variant-swatch').forEach(s => s.classList.remove('active'));
        
        // Add active to clicked
        this.classList.add('active');
        
        // Update main product image if swatch has an image
        const swatchImage = this.dataset.variantImage;
        if (swatchImage) {
          const mainImage = document.querySelector('.product__media-item img');
          if (mainImage) {
            mainImage.src = swatchImage;
          }
        }
      });
    });
  }

  // Mobile Swipe Gestures for Gallery
  initMobileSwipeGestures() {
    if (window.innerWidth > 768) return;
    
    const gallery = document.querySelector('.product__media-wrapper');
    if (!gallery) return;
    
    let touchStartX = 0;
    let touchEndX = 0;
    
    gallery.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });
    
    gallery.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left - next image
        this.navigateGallery('next');
      } else {
        // Swiped right - previous image
        this.navigateGallery('prev');
      }
    }
  }

  navigateGallery(direction) {
    // Hook into existing pagination system or slider
    const currentDot = document.querySelector('.pagination-dot.active');
    if (!currentDot) return;
    
    const dots = Array.from(document.querySelectorAll('.pagination-dot'));
    const currentIndex = dots.indexOf(currentDot);
    
    let targetIndex;
    if (direction === 'next') {
      targetIndex = (currentIndex + 1) % dots.length;
    } else {
      targetIndex = (currentIndex - 1 + dots.length) % dots.length;
    }
    
    dots[targetIndex]?.click();
  }

  // Social Proof Popups
  initSocialProof() {
    const container = document.querySelector('[data-social-proof]');
    if (!container) return;
    
    const notifications = [
      { name: 'Emma from London', action: 'purchased', product: 'this item', time: '2 minutes ago' },
      { name: 'James from Manchester', action: 'added to cart', product: 'this item', time: '5 minutes ago' },
      { name: 'Sophie from Edinburgh', action: 'purchased', product: 'this item', time: '12 minutes ago' }
    ];
    
    let currentIndex = 0;
    
    const showNotification = () => {
      const notification = notifications[currentIndex];
      const popup = document.createElement('div');
      popup.className = 'social-proof-popup';
      popup.innerHTML = `
        <div class="social-proof-popup__content">
          <div class="social-proof-popup__icon">🛍️</div>
          <div class="social-proof-popup__text">
            <strong>${notification.name}</strong> ${notification.action} ${notification.product}
            <div class="social-proof-popup__time">${notification.time}</div>
          </div>
        </div>
      `;
      
      document.body.appendChild(popup);
      
      setTimeout(() => popup.classList.add('visible'), 100);
      setTimeout(() => {
        popup.classList.remove('visible');
        setTimeout(() => popup.remove(), 300);
      }, 5000);
      
      currentIndex = (currentIndex + 1) % notifications.length;
    };
    
    // Show first notification after 10 seconds, then every 30 seconds
    setTimeout(() => {
      showNotification();
      setInterval(showNotification, 30000);
    }, 10000);
  }

  // Back in Stock Alerts
  initBackInStock() {
    const triggers = document.querySelectorAll('[data-back-in-stock-trigger]');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.showBackInStockModal(trigger.dataset.productId, trigger.dataset.variantId);
      });
    });
  }

  showBackInStockModal(productId, variantId) {
    // Create modal if doesn't exist
    let modal = document.querySelector('.back-in-stock-modal');
    
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'back-in-stock-modal';
      modal.innerHTML = `
        <div class="modal-overlay"></div>
        <div class="modal-content">
          <button class="modal-close">&times;</button>
          <h3>Notify Me When Available</h3>
          <p>Enter your email and we'll notify you when this item is back in stock.</p>
          <form class="back-in-stock-form">
            <input type="email" name="email" placeholder="your@email.com" required>
            <input type="hidden" name="product_id" value="${productId}">
            <input type="hidden" name="variant_id" value="${variantId}">
            <button type="submit">Notify Me</button>
          </form>
        </div>
      `;
      document.body.appendChild(modal);
      
      // Close handlers
      modal.querySelector('.modal-close').addEventListener('click', () => {
        modal.classList.remove('active');
      });
      
      modal.querySelector('.modal-overlay').addEventListener('click', () => {
        modal.classList.remove('active');
      });
      
      // Form submission
      modal.querySelector('.back-in-stock-form').addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleBackInStockSubmit(e.target);
      });
    }
    
    modal.classList.add('active');
  }

  handleBackInStockSubmit(form) {
    const formData = new FormData(form);
    const email = formData.get('email');
    
    // Store in localStorage (in production, send to Klaviyo/customer.io)
    const alerts = JSON.parse(localStorage.getItem('kirei_stock_alerts') || '[]');
    alerts.push({
      email,
      productId: formData.get('product_id'),
      variantId: formData.get('variant_id'),
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('kirei_stock_alerts', JSON.stringify(alerts));
    
    this.showToast('✓ You\'ll be notified when this item is back in stock!');
    document.querySelector('.back-in-stock-modal')?.classList.remove('active');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductEnhancedFeatures());
} else {
  new ProductEnhancedFeatures();
}

// Add required CSS for social proof and modals
const enhancedStyles = `
  .social-proof-popup {
    position: fixed;
    bottom: 2rem;
    left: 2rem;
    background: white;
    padding: 1rem;
    border-radius: var(--product-border-radius, 8px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 999;
    transform: translateX(-150%);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    max-width: 320px;
  }
  
  .social-proof-popup.visible {
    transform: translateX(0);
    opacity: 1;
  }
  
  .social-proof-popup__content {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .social-proof-popup__icon {
    font-size: 2rem;
    flex-shrink: 0;
  }
  
  .social-proof-popup__text {
    font-size: 14px;
    line-height: 1.4;
  }
  
  .social-proof-popup__time {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 0.25rem;
  }
  
  .back-in-stock-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: none;
    align-items: center;
    justify-content: center;
  }
  
  .back-in-stock-modal.active {
    display: flex;
  }
  
  .modal-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }
  
  .modal-content {
    position: relative;
    background: white;
    padding: 2rem;
    border-radius: var(--product-border-radius, 8px);
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }
  
  .modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    color: rgba(0, 0, 0, 0.5);
  }
  
  .back-in-stock-form {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .back-in-stock-form input[type="email"] {
    padding: 0.8rem;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: var(--product-border-radius, 8px);
    font-size: 15px;
  }
  
  .back-in-stock-form button {
    padding: 1rem;
    background: var(--color-primary, #000);
    color: white;
    border: none;
    border-radius: var(--product-border-radius, 8px);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.3s;
  }
  
  .back-in-stock-form button:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    .social-proof-popup {
      bottom: 1rem;
      left: 1rem;
      right: 1rem;
      max-width: none;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = enhancedStyles;
document.head.appendChild(styleSheet);

